// Use the manager resource shape, not the public resource shape
import { ENDPOINTS } from '../consts';
import { AbstractApi } from '../../abstract-api';
import { GetSpecialismsResp } from '../types/specialism';
import { GetResourcesPaginatedParamsExtended } from '../types';
import {
  PutSpecialismEvent,
  PostSpecialismEvent,
  PutSpecialismRespBody,
  PostSpecialismRespBody,
  DeleteSpecialismRespBody,
} from '../schema';
import {
  PutResourceInput,
  PostResourceInput,
  ResourceManagerRow,
  Resource as ManagerResource,
  ResourceManagerListResponse,
} from '../types/resource-management.types';

export class ManageApi extends AbstractApi {
  private mapRowToManagerResource(row: ResourceManagerRow): ManagerResource {
    return {
      resource_id: row.id,
      title: row.title,
      url: row.url,
      personal_login: row.personalLogin,
      status: row.status,
      created: row.created,
      updated: row.updated,
      media_type_id: '',
      location_type_id: '',
      mediaType: { media_type_id: '', media_type_description: row.mediaType ?? '' },
      locationType: { location_type_id: '', location_type_description: row.locationType ?? '' },
      skillCategory: (row.skillCategory ?? []).map((sc) => ({
        skill_category_id: sc.id,
        skill_group_id: '',
        title: sc.title,
        ordinal: 0,
        default_description_title: '',
        default_examples_title: '',
      })),
      skillLevel: row.skillLevel,
      roleDepartmentIds: row.roleDepartmentIds ?? [],
      specialisms: (row.specialisms ?? []).map((s) => ({ specialism_id: s.id, title: s.title })),
    };
  }
  async getResources({
    pageNumber = 0,
    pageSize = 0,
    ...rest
  }: Partial<GetResourcesPaginatedParamsExtended> = {}): Promise<{
    resources: ResourceManagerRow[];
    totalCount: number;
  }> {
    const params: GetResourcesPaginatedParamsExtended = { pageNumber, pageSize, ...rest };
    const axios = await this.axios();
    const safeParams = {
      mediaTypes: params.mediaTypes,
      searchQuery: params.searchQuery,
      skillCategories: params.skillCategories,
      isInternal: params.isInternal,
      status: params.status === '' ? undefined : params.status,
      updatedFrom: params.updatedFrom === '' ? undefined : params.updatedFrom,
      updatedTo: params.updatedTo === '' ? undefined : params.updatedTo,
      pageNumber: params.pageNumber,
      pageSize: params.pageSize,
    };
    const { data } = await axios.get<ResourceManagerListResponse>(
      ENDPOINTS.LEARNING.MANAGE.RESOURCES,
      {
        params: safeParams,
      }
    );
    return {
      resources: data.resources,
      totalCount: data.totalCount,
    };
  }

  async postResource(payload: PostResourceInput): Promise<ManagerResource> {
    const axios = await this.axios();
    const { data } = await axios.post<ResourceManagerRow | { resource: ResourceManagerRow }>(
      ENDPOINTS.LEARNING.MANAGE.RESOURCE,
      payload
    );
    const row: ResourceManagerRow = (data as any).resource ?? (data as ResourceManagerRow);
    return this.mapRowToManagerResource(row);
  }

  async putResource(resourceId: string, payload: PutResourceInput): Promise<ManagerResource> {
    const axios = await this.axios();
    const { data } = await axios.put<ResourceManagerRow | { resource: ResourceManagerRow }>(
      `${ENDPOINTS.LEARNING.MANAGE.RESOURCE}/${resourceId}`,
      payload
    );
    const row: ResourceManagerRow = (data as any).resource ?? (data as ResourceManagerRow);
    return this.mapRowToManagerResource(row);
  }

  async patchResourceStatus(resourceId: string, status: string): Promise<ManagerResource> {
    const axios = await this.axios();
    const { data } = await axios.patch<ResourceManagerRow | { resource: ResourceManagerRow }>(
      `${ENDPOINTS.LEARNING.MANAGE.RESOURCE}/${resourceId}/status`,
      { status }
    );
    const row: ResourceManagerRow = (data as any).resource ?? (data as ResourceManagerRow);
    return this.mapRowToManagerResource(row);
  }

  async getSpecialisms(params: {
    pageNumber: number;
    pageSize: number;
    searchQuery?: string;
    skillCategories?: string[];
  }): Promise<GetSpecialismsResp> {
    const axios = await this.axios();
    const { data } = await axios.get<GetSpecialismsResp>(ENDPOINTS.LEARNING.MANAGE.SPECIALISMS, {
      params: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchQuery: params.searchQuery,
        skillCategories:
          params.skillCategories && params.skillCategories?.length > 0
            ? params.skillCategories.join(',')
            : undefined,
      },
    });
    return data;
  }

  async postSpecialism(body: PostSpecialismEvent['body']): Promise<PostSpecialismRespBody> {
    const axios = await this.axios();
    const { data } = await axios.post<PostSpecialismRespBody>(
      ENDPOINTS.LEARNING.MANAGE.SPECIALISM,
      body
    );
    return data;
  }

  async putSpecialism(
    specialismId: string,
    body: PutSpecialismEvent['body']
  ): Promise<PutSpecialismRespBody> {
    const axios = await this.axios();
    const { data } = await axios.put<PutSpecialismRespBody>(
      `${ENDPOINTS.LEARNING.MANAGE.SPECIALISM}/${specialismId}`,
      body
    );
    return data;
  }

  async deleteSpecialism(specialismId: string): Promise<DeleteSpecialismRespBody> {
    const axios = await this.axios();
    const { data } = await axios.delete<DeleteSpecialismRespBody>(
      `${ENDPOINTS.LEARNING.MANAGE.SPECIALISM}/${specialismId}`
    );
    return data;
  }
}
